/**
 * Prazo de produção do laboratório — em dias úteis (sem fim de semana e feriados nacionais; sábado
 * conta se o laboratório trabalha nele) ou em dias corridos. Regra única pra servidor e telas: o
 * prazo efetivo de um pedido, quando ele está atrasado e quando entra no aviso de urgência.
 * Datas no dia local (meia-noite), como o resto do app.
 */
export type PrazoDoLaboratorio = {
  /** Prazo padrão, pra pedido sem prazo combinado. */
  dias: number;
  diasUteis: boolean;
  /** Sábado é dia de trabalho (só vale com `diasUteis`). */
  sabado: boolean;
  /** Avisar quantos dias (de trabalho) antes do prazo; null = sem aviso. */
  avisoAntes: number | null;
};

export const PRAZO_PADRAO: PrazoDoLaboratorio = { dias: 7, diasUteis: true, sabado: false, avisoAntes: 2 };

/** De um LabProfile (ou nada — laboratório fora da plataforma) pro prazo. */
export function prazoDoPerfil(
  p?: { slaDays?: number | null; prazoEmDiasUteis?: boolean | null; trabalhaSabado?: boolean | null; avisoDeUrgencia?: number | null } | null,
): PrazoDoLaboratorio {
  if (!p) return PRAZO_PADRAO;
  return {
    dias: p.slaDays ?? PRAZO_PADRAO.dias,
    diasUteis: p.prazoEmDiasUteis ?? PRAZO_PADRAO.diasUteis,
    sabado: p.trabalhaSabado ?? PRAZO_PADRAO.sabado,
    avisoAntes: p.avisoDeUrgencia === undefined ? PRAZO_PADRAO.avisoAntes : p.avisoDeUrgencia,
  };
}

export const inicioDoDia = (d: Date | string) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const mais = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const chave = (d: Date) => `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;

/** Domingo de Páscoa (algoritmo de Meeus/Jones/Butcher). */
function pascoa(ano: number): Date {
  const a = ano % 19;
  const b = Math.floor(ano / 100);
  const c = ano % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mes = Math.floor((h + l - 7 * m + 114) / 31);
  const dia = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(ano, mes - 1, dia);
}

const cacheDeFeriados = new Map<number, Set<string>>();

/** Feriados nacionais do ano: fixos + Carnaval (seg e ter), Sexta-feira Santa e Corpus Christi. */
export function feriadosNacionais(ano: number): Set<string> {
  const pronto = cacheDeFeriados.get(ano);
  if (pronto) return pronto;
  const fixos = [
    [1, 1],
    [4, 21],
    [5, 1],
    [9, 7],
    [10, 12],
    [11, 2],
    [11, 15],
    [11, 20],
    [12, 25],
  ].map(([m, d]) => new Date(ano, m - 1, d));
  const p = pascoa(ano);
  const moveis = [mais(p, -48), mais(p, -47), mais(p, -2), mais(p, 60)];
  const set = new Set([...fixos, ...moveis].map(chave));
  cacheDeFeriados.set(ano, set);
  return set;
}

export function ehDiaDeTrabalho(d: Date, prazo: PrazoDoLaboratorio): boolean {
  if (!prazo.diasUteis) return true;
  const semana = d.getDay();
  if (semana === 0 || (semana === 6 && !prazo.sabado)) return false;
  return !feriadosNacionais(d.getFullYear()).has(chave(d));
}

/** `inicio` + n dias (de trabalho, com dias úteis; corridos sem). n negativo anda pra trás. */
export function somarDias(inicio: Date | string, n: number, prazo: PrazoDoLaboratorio): Date {
  let d = inicioDoDia(inicio);
  if (!prazo.diasUteis) return mais(d, n);
  const passo = n < 0 ? -1 : 1;
  let faltam = Math.abs(n);
  while (faltam > 0) {
    d = mais(d, passo);
    if (ehDiaDeTrabalho(d, prazo)) faltam--;
  }
  return d;
}

/** O prazo combinado com a clínica ou, sem ele, a criação do pedido + o prazo padrão. */
export function prazoEfetivo(
  pedido: { deadline?: Date | string | null; createdAt: Date | string },
  prazo: PrazoDoLaboratorio,
): Date {
  return pedido.deadline ? inicioDoDia(pedido.deadline) : somarDias(pedido.createdAt, prazo.dias, prazo);
}

/** Dia a partir do qual o pedido entra no aviso de urgência (null = laboratório sem aviso). */
export function inicioDoAviso(efetivo: Date, prazo: PrazoDoLaboratorio): Date | null {
  return prazo.avisoAntes == null ? null : somarDias(efetivo, -prazo.avisoAntes, prazo);
}

export type SituacaoDoPrazo = "atrasado" | "venceEmBreve" | "noPrazo";

/** Atrasado tem precedência; "vence em breve" é a janela do aviso, até o próprio dia do prazo. */
export function situacaoDoPrazo(
  pedido: { deadline?: Date | string | null; createdAt: Date | string },
  prazo: PrazoDoLaboratorio,
  hoje: Date = new Date(),
): SituacaoDoPrazo {
  const dia = inicioDoDia(hoje);
  const efetivo = prazoEfetivo(pedido, prazo);
  if (efetivo < dia) return "atrasado";
  const aviso = inicioDoAviso(efetivo, prazo);
  return aviso && aviso <= dia ? "venceEmBreve" : "noPrazo";
}

/** "7 dias úteis" / "7 dias" — pra texto de tela. */
export const rotuloDoPrazo = (prazo: PrazoDoLaboratorio, dias = prazo.dias) =>
  `${dias} dia${dias === 1 ? "" : "s"}${prazo.diasUteis ? (dias === 1 ? " útil" : " úteis") : ""}`;

const ENCERRADOS = new Set(["CONCLUIDO", "CANCELADO", "RECUSADO"]);

/** Pedido em aberto com o prazo já vencido — a mesma conta no servidor (painel das organizações) e
 *  na tela (lista de pedidos), pra os números baterem. */
export function estaAtrasado(
  pedido: { status: string; deadline?: Date | string | null; createdAt: Date | string },
  prazo: PrazoDoLaboratorio,
  hoje: Date = new Date(),
): boolean {
  if (ENCERRADOS.has(pedido.status)) return false;
  return prazoEfetivo(pedido, prazo) < inicioDoDia(hoje);
}
